import { useContext, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { BsEye, BsEyeSlash } from 'react-icons/bs';
import { RiCloseFill } from 'react-icons/ri';
import ReactTooltip from 'react-tooltip';
import UploadIcon from 'src/assets/img/upload-icon.svg';
import { CheckboxSelection, RadioSelection } from 'src/component';
import { OnboardingPolicyRequest, UserTypes } from 'src/model';
import ApiContext from 'src/provider/API/call-service';
import AppInfoContext from 'src/provider/state-manager/appInfoProvider';
import VisibilityContext from 'src/provider/state-manager/visibilityProvider';
import {
    AbsoluteContainer,
    AppLabel, AppSpan, AppText, Button, CustomContainer, FlexColumn, FlexRow,
    Form, FormGroup, FormGroupWithIcon, ScreenContainer
} from 'src/style';
import helpers from 'src/utils/helpers';
import utility from 'src/utils/utility';
import { explain } from 'src/utils/view';
import {
    ACCEPTED_ID_TYPES_HEADING,
    ACCEPTED_ID_TYPES_HELP_TEXT,
    ACCEPTED_ID_TYPE_OPTIONS,
    EXEMPT_EXISTING_ID_VERIFICATION_HEADING,
    EXEMPT_EXISTING_ID_VERIFICATION_LABEL,
    ONBOARDING_TERMS_URL,
    NIN_ID_KEY,
} from 'src/utils/onboardingPolicyCopy';
import { TermsAcceptanceModal, NinEnforcementModal, ExemptVerificationModal } from 'src/componentv2';
import './style.scss';


const Configuration = () => {
    const { notifier } = useContext(VisibilityContext)
    const { t } = useTranslation()
    const { API } = useContext(ApiContext)
    const { info: { userType, institutionData } } = useContext(AppInfoContext)
    const [userRole, setUserRole] = useState('')
    const [input, setInput] = useState({
        showTx: true, correspondenceEmail: '', correspondencePhone: '', privacyPolicyUrl: '', playstoreEmail: '',
        address: '', defaultAccountOpeningProductCode: '', defaultAccountOfficerCode: '', playstorePassword: '',
        ussdCode: '*220*1#', website: '', allowedAccountProductCodes: '27', disallowedAccountProductCodes: '',
        testAccountNumber: '', testAccountCustomerID: '', testAccountUsername: '', showTransactions: true,
        twoFactorThresholdAmount: '', emailSenderID: '', maximumDailyTransactionLimit: '', maximumOneTimeTransactionLimit: '',
        defaultOneTimeTransactionLimit: '', defaultDailyTransactionLimit: '', disallowedFixedDepositProductCodes: '', inactivityTime: '',
        termsAndConditionsUrl: '', loanPolicyUrl: '', appzoneUsername: '', appzoneAccountNumber: '', appzoneCustomerID: '',
        recovaInstitutionCode: '', androidAppUrl: '', iosAppUrl: '', stamp: '', stampExtension: '', hideWithdrawableBalance: false, hideAvailableBalance: false,
        ebanqoAgentCode: '', ebanqoUrlPath: '', ebanqoUsername: '', ebanqoPassword: '', useIdemityForAccountLimitChange: false,
        accountLimitIdemity: '', useVerifyMeForOnboarding: false, phoneNumberLength: '', accountNumberLength: '',
        paymentItemIsHidden: false, useAirtimeAsBills: false, hasLiveChat: false, usePINforLogin: false, useVerifyMeForTransactions: false,
        cardRequestType: '', disallowedLoanProductCodes: '', cardFee: ''
    })

    const [onboardingPolicyInput, setOnboardingPolicyInput] = useState<OnboardingPolicyRequest>({
        allowArbitraryPhoneNumber: false,
        enforceFaceMatchAtOnboarding: false,
        allowIdVerificationWithMetadata: false,
        exemptIdVerificationForExistingCustomers: false,
        acceptedIds: ['BVN']
    })

    const toggleAcceptedId = (key: string) => {
        setOnboardingPolicyInput((prevInput) => ({
            ...prevInput,
            acceptedIds: prevInput.acceptedIds.includes(key)
                ? prevInput.acceptedIds.filter((id) => id !== key)
                : [...prevInput.acceptedIds, key]
        }))
    }

    const [termsOpen, setTermsOpen] = useState(false)
    // Forces the phone-number checkbox to remount (re-syncing its internal checked state
    // from `value`) when the terms prompt is dismissed without agreeing.
    const [phoneCheckboxResetKey, setPhoneCheckboxResetKey] = useState(0)

    const [ninConfirmed, setNinConfirmed] = useState(false)
    const [ninModalOpen, setNinModalOpen] = useState(false)
    // Forces the NIN checkbox to remount (re-syncing its internal checked state from
    // `value`) when the BankOne CBA acknowledgement is dismissed without confirming.
    const [ninCheckboxResetKey, setNinCheckboxResetKey] = useState(0)

    const handleAcceptedIdChange = (key: string) => {
        if (key === NIN_ID_KEY && !onboardingPolicyInput.acceptedIds.includes(NIN_ID_KEY)) {
            if (ninConfirmed) {
                toggleAcceptedId(key)
            } else {
                setNinModalOpen(true)
            }
            return
        }
        toggleAcceptedId(key)
    }

    const handleConfirmNin = () => {
        setNinConfirmed(true)
        setOnboardingPolicyInput((prevInput) => (
            prevInput.acceptedIds.includes(NIN_ID_KEY)
                ? prevInput
                : { ...prevInput, acceptedIds: [...prevInput.acceptedIds, NIN_ID_KEY] }
        ))
        setNinModalOpen(false)
    }

    const handleDeclineNin = () => {
        setNinModalOpen(false)
        setNinCheckboxResetKey((key) => key + 1)
    }

    const [exemptModalOpen, setExemptModalOpen] = useState(false)
    // Forces the exemption checkbox to remount (re-syncing its internal checked state from
    // `value`) when the risk acknowledgement is dismissed without confirming.
    const [exemptCheckboxResetKey, setExemptCheckboxResetKey] = useState(0)

    // Exempting returning customers is security-sensitive, so enabling it prompts for an explicit
    // risk acknowledgement first. Disabling it is immediate.
    const handleExemptChange = (checked: boolean) => {
        if (checked && !onboardingPolicyInput.exemptIdVerificationForExistingCustomers) {
            setExemptModalOpen(true)
            return
        }
        setOnboardingPolicyInput((prevInput) => ({
            ...prevInput,
            exemptIdVerificationForExistingCustomers: checked,
        }))
    }

    const handleConfirmExempt = () => {
        setOnboardingPolicyInput((prevInput) => ({
            ...prevInput,
            exemptIdVerificationForExistingCustomers: true,
        }))
        setExemptModalOpen(false)
    }

    const handleDeclineExempt = () => {
        setExemptModalOpen(false)
        // The checkbox flips itself optimistically on click; remount it so its visible state
        // re-syncs with the unchanged (still false) exemption value.
        setExemptCheckboxResetKey((key) => key + 1)
    }

    // Streamlined onboarding (arbitrary phone number entry) is the only mode that requires
    // Terms and Conditions sign-off, so enabling that checkbox prompts for agreement first.
    const handleAllowArbitraryPhoneNumberChange = (checked: boolean) => {
        if (!checked) {
            setOnboardingPolicyInput((prevInput) => ({
                ...prevInput,
                allowArbitraryPhoneNumber: false,
            }))
            return
        }

        setTermsOpen(true)
    }

    const handleAcceptTerms = () => {
        setOnboardingPolicyInput((prevInput) => ({
            ...prevInput,
            allowArbitraryPhoneNumber: true,
            enforceFaceMatchAtOnboarding: true,
        }))
        setTermsOpen(false)
    }

    const handleDeclineTerms = () => {
        setTermsOpen(false)
        // The checkbox flips itself optimistically on click; remount it so its visible
        // state re-syncs with the unchanged (still false) allowArbitraryPhoneNumber value.
        setPhoneCheckboxResetKey((key) => key + 1)
    }

    const [imgExtra, setImgExtra] = useState({
        stampName: '', stampUrl: '',
        stampSize: ''
    })
    const [inputsDisabled, setInputsDisabled] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [])

    useEffect(() => {
        if (institutionData) {
            const {
                correspondenceEmail, correspondencePhone, defaultAccountOpeningProductCode, defaultAccountOfficerCode,
                allowedAccountProductCodes, disallowedAccountProductCodes, testAccountNumber, testAccountCustomerID,
                testAccountUsername, showTransactions, twoFactorThresholdAmount, emailSenderID, maximumDailyTransactionLimit,
                maximumOneTimeTransactionLimit, defaultOneTimeTransactionLimit, defaultDailyTransactionLimit,
                privacyPolicyUrl, termsAndConditionsUrl, loanPolicyUrl, appzoneUsername, appzoneAccountNumber,website,
                appzoneCustomerID, stamp, hideWithdrawableBalance, hideAvailableBalance, address, ebanqoCredential,
                recovaCredential, useIdemityForAccountLimitChange, accountLimitIdemity, useVerifyMeForOnboarding, phoneNumberLength,
                accountNumberLength, paymentItemIsHidden, useAirtimeAsBills, hasLiveChat, usePINforLogin, useVerifyMeForTransactions,
                cardRequestType, disallowedFixedDepositProductCodes, disallowedLoanProductCodes, cardFee, androidAppUrl, iosAppUrl
            } = institutionData

            setInput({
                ...input, correspondenceEmail, correspondencePhone, defaultAccountOpeningProductCode, defaultAccountOfficerCode,
                allowedAccountProductCodes, disallowedAccountProductCodes, testAccountNumber, testAccountCustomerID,
                testAccountUsername, showTransactions, twoFactorThresholdAmount, emailSenderID, maximumDailyTransactionLimit,
                maximumOneTimeTransactionLimit, defaultOneTimeTransactionLimit, defaultDailyTransactionLimit,
                privacyPolicyUrl, termsAndConditionsUrl, loanPolicyUrl, appzoneUsername, appzoneAccountNumber, website,
                appzoneCustomerID, stamp, recovaInstitutionCode: recovaCredential?.recovaInstitutionCode,
                androidAppUrl: androidAppUrl || '', iosAppUrl: iosAppUrl || '',
                hideWithdrawableBalance: !hideWithdrawableBalance ? false : true,
                hideAvailableBalance: !hideAvailableBalance ? false : true,
                ebanqoAgentCode: ebanqoCredential?.agentCode, ebanqoUrlPath: ebanqoCredential?.urlPath,
                ebanqoUsername: ebanqoCredential?.username, ebanqoPassword: ebanqoCredential?.password,
                address, useIdemityForAccountLimitChange: useIdemityForAccountLimitChange ? true : false, accountLimitIdemity,
                useVerifyMeForOnboarding: useVerifyMeForOnboarding ? true : false, phoneNumberLength, accountNumberLength,
                paymentItemIsHidden: paymentItemIsHidden ? true : false, useAirtimeAsBills: useAirtimeAsBills ? true : false,
                hasLiveChat: hasLiveChat ? true : false, usePINforLogin: usePINforLogin ? true : false, useVerifyMeForTransactions: useVerifyMeForTransactions ? true : false,
                cardRequestType, disallowedFixedDepositProductCodes, disallowedLoanProductCodes, cardFee
            })


            const stampName = stamp?.substring(stamp?.lastIndexOf('/') + 1)?.split('?')[0];

            setImgExtra({
                ...imgExtra, stampUrl: stamp, stampName
            })
        }

        if (userType) {
            setUserRole(userType)
            if (userType != UserTypes.ADMIN) setInputsDisabled(true)
        }
    }, [institutionData, userType])

    const handleInput = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;

        let trimmedValue = value;
        if (
            name === "testAccountUsername" ||
            name === "testAccountCustomerID" ||
            name === "testAccountNumber"
        )
            trimmedValue = value.trim();

        setInput({ ...input, [name]: trimmedValue });
    };

    const handleAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/,/g, '')
        if (!isNaN(Number(val))) {
            setInput({ ...input, [e.target.name]: val })
        }
    }

    async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
        if (e.target.files instanceof FileList) {
            if (!helpers.isValidFileType(e.target.value)) return notifier.show(t('configuration.errors.invalidFileType'))

            const extension = helpers.getFileExtension(e.target.value)

            const uploadedFile = await helpers.compressImage(e.target.files[0])
            if (!uploadedFile) return notifier.show(t('configuration.errors.compressionFailed'))

            helpers.convertFileToBase64(uploadedFile, true)
                .then(async (result: any) => {
                    const dimensions = result.dimensions
                    const { isValidResolution, message } = helpers.validateImgResolution(e.target.name, dimensions)

                    if (!isValidResolution) {
                        return notifier.show(message)
                    }

                    setInput({
                        ...input,
                        [e.target.name]: result.baseURL,
                        [e.target.alt]: extension
                    })

                    setImgExtra({
                        ...imgExtra,
                        [`${e.target.name}Name`]: e.target.files![0]?.name,
                        [`${e.target.name}Url`]: URL.createObjectURL(e.target.files![0]),
                        [`${e.target.name}Size`]: (e.target.files![0]?.size / 1024).toFixed(1),
                    })
                })
                .catch(err => {
                    notifier.show(JSON.stringify(err), t('configuration.errors.validationError'))
                })
        }
    }

    const handleInputLength = (e: React.ChangeEvent<HTMLInputElement>) => {
        let { name, value } = e.target;

        if (Number(value) < 1) value = '';
        if (value.length >= 20) value = value.slice(0, 20);

        setInput((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    function handleSubmit(e: React.FormEvent<HTMLButtonElement | HTMLFormElement>) {
        e.preventDefault()

        const { isValidated, message } = helpers.inputValidator(input, [], ['privacyPolicyUrl', 'termsAndConditionsUrl'])
        if (!isValidated) return notifier.show(message)

        const invalidUrlMessage = helpers.validateConfigUrls(input)
        if (invalidUrlMessage) return notifier.show(invalidUrlMessage)

        API.updateConfiguration({
            ...input,
            accountNumberLength: input.accountNumberLength || 10,
            phoneNumberLength: input.phoneNumberLength || 11,
        });
    }

    useEffect(() => {
        const fetchOnboardingPolicy = async () => {
            const response = await API.fetchOnboardingPolicy()
            if (response) {
                const acceptedIds = response?.acceptedIds?.length ? response.acceptedIds : ['BVN']
                setOnboardingPolicyInput({
                    allowArbitraryPhoneNumber: response?.allowArbitraryPhoneNumber,
                    enforceFaceMatchAtOnboarding: response?.enforceFaceMatchAtOnboarding,
                    allowIdVerificationWithMetadata: response?.allowIdVerificationWithMetadata,
                    exemptIdVerificationForExistingCustomers: !!response?.exemptIdVerificationForExistingCustomers,
                    acceptedIds
                })
                if (acceptedIds.includes(NIN_ID_KEY)) setNinConfirmed(true)
            }
        }
        fetchOnboardingPolicy()

    }, [])

    async function submitOnboardingPolicy(e: React.FormEvent<HTMLButtonElement | HTMLFormElement>) {
        e.preventDefault()
        if (onboardingPolicyInput.acceptedIds.length === 0) return
        await API.setOnboardingPolicy(onboardingPolicyInput)
    }


    return (
        <ScreenContainer>
            <AppText textSize='1.9' color='#101828' fontWeight="700">{t('configuration.title')}</AppText>
            <AppText fontWeight='600' topMargin='2' >{t('configuration.subtitle')}</AppText>
            <Form>
                <CustomContainer topMargin='2'>

                    <AppLabel color='#101828' textSize='1.8' fontWeight='800' bottomMargin='2'>
                        Onboarding Flow Settings
                    </AppLabel>


                            <FlexColumn alignItems='flex-start'>
                                <FlexRow justifyContent='space-between'
                                    className='control-box-container'>
                                    <CustomContainer topMargin='2'>
                                        <AppLabel fontWeight='600' textSize='1.3'>Streamlined Onboarding</AppLabel>
                                        <CustomContainer
                                            justifyContent='space-between' wrap='wrap' className='checkbox-container'>
                                            <CheckboxSelection
                                                key={phoneCheckboxResetKey}
                                                name={"Allow users enter their preferred phone number for verification"}
                                                customClass='single-balance-control-box'
                                                output={handleAllowArbitraryPhoneNumberChange}
                                                value={onboardingPolicyInput.allowArbitraryPhoneNumber}
                                                disabled={userRole != UserTypes.ADMIN}
                                            />
                                            <div className="checkbox-icon" style={{marginTop:"5px"}}>
                                                {explain(
                                                    "When enabled, users can enter any phone number during onboarding. When disabled, users must use the phone number linked to your BVN or NIN.", true, '0'
                                                )}
                                            </div>
                                        </CustomContainer>
                                    </CustomContainer>

                                    <CustomContainer topMargin='2' leftMargin='2'>
                                        <AppLabel fontWeight='600' textSize='1.3'>Enforce Id Verification At Onboarding</AppLabel>
                                        <CustomContainer
                                            justifyContent='space-between'  wrap='wrap' className='checkbox-container'>
                                            <CheckboxSelection
                                                name={"Enforce Id Verification"}
                                                customClass='single-balance-control-box'
                                                output={() =>
                                                    setOnboardingPolicyInput((prevInput) => ({
                                                        ...prevInput,
                                                        enforceFaceMatchAtOnboarding: !prevInput.enforceFaceMatchAtOnboarding,
                                                    }))
                                                }
                                                value={onboardingPolicyInput.enforceFaceMatchAtOnboarding}
                                                disabled={userRole != UserTypes.ADMIN || onboardingPolicyInput?.allowArbitraryPhoneNumber === true}
                                            />
                                            <div className="checkbox-icon" style={{marginTop:"5px"}}>
                                                {explain(
                                                    t('configuration.verifyMeOnboarding.tooltip'), true, '0'
                                                )}
                                            </div>
                                        </CustomContainer>
                                    </CustomContainer>
                                </FlexRow>

                                <FlexRow justifyContent='flex-start'
                                    className='control-box-container'>
                                    <CustomContainer topMargin='2'>
                                        <AppLabel fontWeight='600' textSize='1.3'>{EXEMPT_EXISTING_ID_VERIFICATION_HEADING}</AppLabel>
                                        <CustomContainer
                                            justifyContent='space-between' wrap='wrap' className='checkbox-container'>
                                            <CheckboxSelection
                                                key={exemptCheckboxResetKey}
                                                name={EXEMPT_EXISTING_ID_VERIFICATION_LABEL}
                                                customClass='single-balance-control-box'
                                                output={handleExemptChange}
                                                value={onboardingPolicyInput.exemptIdVerificationForExistingCustomers}
                                                disabled={userRole != UserTypes.ADMIN}
                                            />
                                        </CustomContainer>
                                    </CustomContainer>
                                </FlexRow>

                                <FlexRow justifyContent='flex-start'
                                    className='control-box-container'>
                                    <CustomContainer topMargin='2'>
                                        <AppLabel fontWeight='600' textSize='1.3'>{ACCEPTED_ID_TYPES_HEADING}</AppLabel>
                                        <AppText color='#667085' textSize='1.2' topMargin='0.4' bottomMargin='0.8'>
                                            {ACCEPTED_ID_TYPES_HELP_TEXT}
                                        </AppText>
                                        <CustomContainer
                                            justifyContent='space-between' wrap='wrap' className='checkbox-container'>
                                            {ACCEPTED_ID_TYPE_OPTIONS.map((option) => (
                                                <CheckboxSelection
                                                    key={option.key === NIN_ID_KEY ? `${option.key}-${ninCheckboxResetKey}` : option.key}
                                                    name={option.label}
                                                    customClass='single-balance-control-box'
                                                    output={() => handleAcceptedIdChange(option.key)}
                                                    value={onboardingPolicyInput.acceptedIds.includes(option.key)}
                                                    disabled={userRole != UserTypes.ADMIN}
                                                />
                                            ))}
                                        </CustomContainer>
                                        {onboardingPolicyInput.acceptedIds.length === 0 && (
                                            <AppText color='#D92D20' textSize='1.2' topMargin='0.8'>
                                                Select at least one ID type.
                                            </AppText>
                                        )}
                                    </CustomContainer>
                                </FlexRow>

                        {userRole === UserTypes.ADMIN &&
                            <FlexRow justifyContent='flex-end' topMargin='1'>
                                <Button
                                    width='8'
                                    titleSize='1.4' fontWeight='600'
                                    height='4' radius='0.8'
                                    bottomMargin='3' hoverColor='#0D968F'
                                    hoverBgColor='#fff' borderColor='#0D968F'
                                    disabledBgColor='#D0D5DD/50'
                                    disabled={onboardingPolicyInput.acceptedIds.length === 0}
                                    onClick={submitOnboardingPolicy}
                                >
                                    {t('configuration.buttons.submit')}
                                </Button>
                            </FlexRow>
                        }
                            </FlexColumn>


                    {/* Hide verify me for variant 2 and 3  */}
                    {/* {institutionData?.design === 3 || institutionData?.design === 2 && (
                        <FlexRow justifyContent='space-between'
                            className='control-box-container'>
                            <CustomContainer topMargin='2'>
                                <AppLabel fontWeight='600' textSize='1.3'>{t('configuration.verifyMeTransactions.label')}</AppLabel>
                                <CustomContainer
                                    justifyContent='space-between' wrap='wrap' className='checkbox-container'>
                                    <CheckboxSelection
                                        name={t('configuration.verifyMeTransactions.checkboxLabel')}
                                        customClass='single-balance-control-box'
                                        output={() =>
                                            setInput((prevInput) => ({
                                                ...prevInput,
                                                useVerifyMeForTransactions: !prevInput.useVerifyMeForTransactions,
                                                useVerifyMeForOnboarding: prevInput.useVerifyMeForTransactions ? true : false, // If disabling, enable onboarding
                                            }))
                                        }
                                        value={input.useVerifyMeForTransactions}
                                        disabled={userRole != UserTypes.ADMIN || (!input.useVerifyMeForOnboarding && input.useVerifyMeForTransactions)}
                                    />
                                    <div className="checkbox-icon">
                                        {explain(
                                            t('configuration.verifyMeTransactions.tooltip'), true, '0'
                                        )}
                                    </div>
                                </CustomContainer>
                            </CustomContainer>

                            <CustomContainer topMargin='2' leftMargin='2'>
                                <AppLabel fontWeight='600' textSize='1.3'>{t('configuration.verifyMeOnboarding.label')}</AppLabel>
                                <CustomContainer
                                    justifyContent='space-between' wrap='wrap' className='checkbox-container'>
                                    <CheckboxSelection
                                        name={t('configuration.verifyMeOnboarding.checkboxLabel')}
                                        customClass='single-balance-control-box'
                                        output={() =>
                                            setInput((prevInput) => ({
                                                ...prevInput,
                                                useVerifyMeForOnboarding: !prevInput.useVerifyMeForOnboarding,
                                                useVerifyMeForTransactions: prevInput.useVerifyMeForOnboarding ? true : false, // If disabling, enable transactions
                                            }))
                                        }
                                        value={input.useVerifyMeForOnboarding}
                                        disabled={userRole != UserTypes.ADMIN || (input.useVerifyMeForOnboarding && !input.useVerifyMeForTransactions)}
                                    />
                                    <div className="checkbox-icon">
                                        {explain(
                                            t('configuration.verifyMeOnboarding.tooltip'), true, '0'
                                        )}
                                    </div>
                                </CustomContainer>
                            </CustomContainer>
                        </FlexRow>
                    )} */}



                    <FormGroupWithIcon>
                        <label>{t('configuration.accountOpeningProductCode.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.accountOpeningProductCode.placeholder')}
                                name='defaultAccountOpeningProductCode'
                                value={input.defaultAccountOpeningProductCode}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            {/* <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    `This is the notification email`, true, '0'
                                )}
                            </CustomContainer> */}
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.accountOfficerCode.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.accountOfficerCode.placeholder')}
                                name='defaultAccountOfficerCode'
                                value={input.defaultAccountOfficerCode}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            {/* <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    `This is the account officer code`, true, '0'
                                )}
                            </CustomContainer> */}
                        </div>
                    </FormGroupWithIcon>




                    <FormGroupWithIcon topMargin='4'>
                        <label>{t('configuration.notificationEmail.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.notificationEmail.placeholder')}
                                name='emailSenderID'
                                value={input.emailSenderID}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.notificationEmail.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.contactEmail.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.contactEmail.placeholder')}
                                name='correspondenceEmail'
                                value={input.correspondenceEmail}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.contactEmail.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.contactPhone.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.contactPhone.placeholder')}
                                name='correspondencePhone'
                                value={input.correspondencePhone}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.contactPhone.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroup>
                        <label>{t('configuration.address.label')}</label>
                        <textarea
                            placeholder={t('configuration.address.placeholder')}
                            name='address'
                            value={input.address}
                            onChange={handleInput}
                            autoComplete='off'
                            required
                            disabled={inputsDisabled}
                        />
                    </FormGroup>
                    <FormGroupWithIcon>
                        <label>{t('configuration.website.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.website.placeholder')}
                                name='website'
                                value={input.website}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.website.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>



                    {/* <FormGroupWithIcon>
                        <label>{t('configuration.cardRequestType.label')}</label>
                        <div className="input-with-icon">
                            <select name="cardRequestType"
                                value={input?.cardRequestType}
                                onChange={handleInput}
                                required
                            >
                                <option value={""}>{t('configuration.cardRequestType.placeholder')}</option>
                                {cardRequestType?.map((requestType: any, index: number) => (
                                    <option key={index} value={requestType?.value}>
                                        {requestType?.label}
                                    </option>
                                ))}
                            </select>
                            <FlexRow justifyContent="flex-end">
                                <FiChevronDown size={20} color='#98A2B3' style={{position:"relative", top:10, right:5}}/>
                            </FlexRow>
                        </div>
                    </FormGroupWithIcon> */}

                    <FormGroupWithIcon>
                        <label>{t('configuration.appzoneAccountNumber.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.appzoneAccountNumber.placeholder')}
                                name='appzoneAccountNumber'
                                value={input.appzoneAccountNumber}
                                onChange={handleInput}
                                autoComplete='off'
                                //required
                                disabled={inputsDisabled}
                            />
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.appzoneCustomerID.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.appzoneCustomerID.placeholder')}
                                name='appzoneCustomerID'
                                value={input.appzoneCustomerID}
                                onChange={handleInput}
                                autoComplete='off'
                                //required
                                disabled={inputsDisabled}
                            />
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.appzoneUsername.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.appzoneUsername.placeholder')}
                                name='appzoneUsername'
                                value={input.appzoneUsername}
                                onChange={handleInput}
                                autoComplete='off'
                                //required
                                disabled={inputsDisabled}
                            />
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.twoFactorThreshold.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.twoFactorThreshold.placeholder')}
                                name='twoFactorThresholdAmount'
                                value={input.twoFactorThresholdAmount ? utility.moneyFormat(input.twoFactorThresholdAmount) : ''}
                                onChange={handleAmount}
                                autoComplete="off"
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.twoFactorThreshold.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>

                    <FormGroupWithIcon>
                        <label>Card Fee</label>
                        <div className="input-with-icon">
                            <input
                                placeholder="Enter card fee"
                                name='cardFee'
                                value={input.cardFee ? utility.moneyFormat(input.cardFee) : ''}
                                onChange={handleAmount}
                                autoComplete="off"
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    `This is the fee applied to each card request.`, true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>

                    <FormGroupWithIcon>
                        <label>{t('configuration.privacyPolicyUrl.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.privacyPolicyUrl.placeholder')}
                                name='privacyPolicyUrl'
                                value={input.privacyPolicyUrl}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.twoFactorThreshold.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.termsConditionsUrl.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.termsConditionsUrl.placeholder')}
                                name='termsAndConditionsUrl'
                                value={input.termsAndConditionsUrl}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.termsConditionsUrl.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.loanPolicyUrl.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.loanPolicyUrl.placeholder')}
                                name='loanPolicyUrl'
                                value={input.loanPolicyUrl}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.loanPolicyUrl.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.recovaInstitutionCode.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.recovaInstitutionCode.placeholder')}
                                name='recovaInstitutionCode'
                                value={input.recovaInstitutionCode}
                                onChange={handleInput}
                                autoComplete='off'
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.recovaInstitutionCode.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.androidAppUrl.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.androidAppUrl.placeholder')}
                                name='androidAppUrl'
                                value={input.androidAppUrl}
                                onChange={handleInput}
                                autoComplete='off'
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.androidAppUrl.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.iosAppUrl.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.iosAppUrl.placeholder')}
                                name='iosAppUrl'
                                value={input.iosAppUrl}
                                onChange={handleInput}
                                autoComplete='off'
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.iosAppUrl.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                </CustomContainer>
                <CustomContainer>
                    <AppLabel color='#101828' textSize='1.8' fontWeight='700' bottomMargin='2'>
                        {t('configuration.googlePlay')}
                    </AppLabel>
                    <FormGroupWithIcon>
                        <label>{t('configuration.testAccountUsername.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.testAccountUsername.placeholder')}
                                name='testAccountUsername'
                                value={input.testAccountUsername}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.testAccountUsername.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.testAccountNumber.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.testAccountNumber.placeholder')}
                                type='number'
                                name='testAccountNumber'
                                value={input.testAccountNumber}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.testAccountNumber.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.testCustomerID.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.testCustomerID.placeholder')}
                                name='testAccountCustomerID'
                                value={input.testAccountCustomerID}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.testCustomerID.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                </CustomContainer>
                <CustomContainer>
                    <AppLabel color='#101828' textSize='1.8' fontWeight='700' bottomMargin='2'>
                        {t('configuration.fixedDeposit')}
                    </AppLabel>

                    {/* <FormGroupWithIcon>
                        <label>Fixed Deposit Product Code</label>
                        <div className="input-with-icon">
                            <select 
                                placeholder="Select product"
                                name='fixedDepositProductCode'
                                value={input.fixedDepositProductCode}
                                onChange={handleInput}
                                autoComplete='off'
                                //required
                                disabled={inputsDisabled}
                            >
                                <option value="">Select product</option>
                            </select>
                            <CustomContainer width='auto' sizeUnit='' className="select-explainer" >
                                {explain(
                                    `This is the fixed deposit product code`, true, '0'
                                )}
                            </CustomContainer>
                            <FiChevronDown className="input-with-icon-tag" />
                        </div>
                    </FormGroupWithIcon> */}
                    <FormGroupWithIcon>
                        <label>{t('configuration.fixedDepositProductCode.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.fixedDepositProductCode.placeholder')}
                                name='disallowedFixedDepositProductCodes'
                                value={input.disallowedFixedDepositProductCodes}
                                onChange={handleInput}
                                autoComplete='off'
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.fixedDepositProductCode.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.disallowedProductCodes.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.disallowedProductCodes.placeholder')}
                                name='disallowedAccountProductCodes'
                                value={input.disallowedAccountProductCodes}
                                onChange={handleInput}
                                autoComplete='off'
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.disallowedProductCodes.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>

                    <FormGroupWithIcon>
                        <label>{t('configuration.disallowedLoan.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.disallowedLoan.placeholder')}
                                name='disallowedLoanProductCodes'
                                value={input.disallowedLoanProductCodes}
                                onChange={handleInput}
                                autoComplete='off'
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.disallowedLoan.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                </CustomContainer>

                <CustomContainer>
                    <AppLabel color='#101828' textSize='1.8' fontWeight='700' bottomMargin='2'>
                        {t('configuration.transactionLimits')}
                    </AppLabel>

                    <FormGroupWithIcon>
                        <label>{t('configuration.defaultOneTimeLimit.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.defaultOneTimeLimit.placeholder')}
                                name='defaultOneTimeTransactionLimit'
                                value={input.defaultOneTimeTransactionLimit ? utility.moneyFormat(input.defaultOneTimeTransactionLimit) : ''}
                                onChange={handleAmount}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.defaultOneTimeLimit.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.defaultDailyLimit.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.defaultDailyLimit.placeholder')}
                                name='defaultDailyTransactionLimit'
                                value={input.defaultDailyTransactionLimit ? utility.moneyFormat(input.defaultDailyTransactionLimit) : ''}
                                onChange={handleAmount}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.defaultDailyLimit.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.maximumOneTimeLimit.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.maximumOneTimeLimit.placeholder')}
                                name='maximumOneTimeTransactionLimit'
                                value={input.maximumOneTimeTransactionLimit ? utility.moneyFormat(input.maximumOneTimeTransactionLimit) : ''}
                                onChange={handleAmount}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.maximumOneTimeLimit.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.maximumDailyLimit.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.maximumDailyLimit.placeholder')}
                                name='maximumDailyTransactionLimit'
                                value={input.maximumDailyTransactionLimit ? utility.moneyFormat(input.maximumDailyTransactionLimit) : ''}
                                onChange={handleAmount}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.maximumDailyLimit.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.inactivityTimeout.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.inactivityTimeout.placeholder')}
                                name='inactivityTime'
                                type='number'
                                value={input.inactivityTime}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.inactivityTimeout.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>

                    <CustomContainer topMargin='2'>
                        <AppLabel fontWeight='600' textSize='1.3'>{t('configuration.indemnitySettings.label')}</AppLabel>
                        <CustomContainer
                            justifyContent='space-between' wrap='wrap'
                            className='control-box-container'
                        >
                            <CheckboxSelection
                                name={t('configuration.indemnitySettings.checkboxLabel')}
                                customClass='single-balance-control-box'
                                output={() => setInput((prevInput) => ({
                                    ...prevInput,
                                    useIdemityForAccountLimitChange: !prevInput.useIdemityForAccountLimitChange
                                }))}
                                value={input.useIdemityForAccountLimitChange}
                                disabled={userRole != UserTypes.ADMIN}
                            />

                            {input.useIdemityForAccountLimitChange && (
                                <FormGroup>
                                    <label>{t('configuration.indemnitySettings.declarationLabel')}</label>
                                    <textarea
                                        placeholder={t('configuration.indemnitySettings.declarationPlaceholder')}
                                        name='accountLimitIdemity'
                                        value={input.accountLimitIdemity}
                                        onChange={handleInput}
                                        autoComplete='off'
                                        required={false}
                                        disabled={inputsDisabled}
                                        maxLength={1000}

                                    />
                                </FormGroup>
                            )}

                        </CustomContainer>
                    </CustomContainer>

                    <CustomContainer bottomMargin='1'>
                        <AppLabel color='#344054' fontWeight='600' bottomMargin='0.8'>{t('configuration.showTransactions.label')}</AppLabel>
                        <RadioSelection
                            option1={t('configuration.showTransactions.option1')}
                            option2={t('configuration.showTransactions.option2')}
                            disabled={inputsDisabled}
                            output={(val: any) => setInput({ ...input, showTransactions: val === 'Show' })}
                        />
                    </CustomContainer>
                </CustomContainer>
                <CustomContainer>
                    <AppLabel color='#101828' textSize='1.8' fontWeight='700'>
                        {t('configuration.statement')}
                    </AppLabel>

                    <CustomContainer topMargin='2'>
                        <AppLabel fontWeight='600' textSize='1.3'>{t('configuration.statementStamp.label')}</AppLabel>
                        {!input.stamp ?
                            <FlexColumn
                                borderColor='#EAECF0' radius='0.8' minHeight='13' topMargin='0.7'
                                padding='1.5'
                            >
                                <img src={UploadIcon} />
                                <input
                                    type="file" name='stamp' alt='stampExtension'
                                    onChange={handleFile}
                                    className="logo-input"
                                    id="stamp"
                                    disabled={inputsDisabled}
                                />
                                <AppLabel color='#0A756F' fontWeight='600' topMargin='1' htmlFor='stamp' align='center'>
                                    {t('configuration.statementStamp.uploadText')}
                                </AppLabel>
                                <AppSpan color='#667085' textSize='1.2' topMargin='0.3'>{t('configuration.statementStamp.fileFormat')}</AppSpan>
                            </FlexColumn>
                            :
                            <CustomContainer
                                borderColor='#0D968F' radius='0.8'
                                padding='1.5' minHeight='13' topMargin='0.7'
                                bgColor={!inputsDisabled ? 'transparent' : '#F7F8F9'}
                            >
                                <FlexRow
                                    justifyContent='space-between'
                                    gap='2' bottomMargin='1'
                                >
                                    <CustomContainer width='6.8' height='3.1'>
                                        <img
                                            src={imgExtra.stampUrl} className='ads-img'
                                            style={{ objectFit: 'contain', objectPosition: 'left' }}
                                        />
                                    </CustomContainer>
                                    {!inputsDisabled &&
                                        <AppSpan
                                            hoverColor='#B42318' cursor='pointer'
                                            onClick={() => setInput({ ...input, stamp: '' })}
                                        >
                                            <RiCloseFill fontSize='2rem' />
                                        </AppSpan>
                                    }
                                </FlexRow>
                                <AppText fontWeight='600'>{imgExtra.stampName}</AppText>
                                {imgExtra.stampSize &&
                                    <AppSpan color='#667085'>{imgExtra.stampSize} KB</AppSpan>
                                }
                            </CustomContainer>
                        }
                    </CustomContainer>
                </CustomContainer>
                <CustomContainer topMargin='2'>
                    <AppLabel color='#101828' textSize='1.8' fontWeight='700'>
                        {t('configuration.balanceDisplayLabel')}
                    </AppLabel>

                    <CustomContainer topMargin='2'>
                        <AppLabel fontWeight='600' textSize='1.3'> {t('configuration.balanceDisplay.label')}</AppLabel>
                        <FlexRow
                            justifyContent='space-between' wrap='wrap'
                            className='control-box-container'
                        >
                            <CheckboxSelection
                                name={t('configuration.balanceDisplay.availableBalance')}
                                customClass='balance-control-box'
                                output={(val: boolean) => setInput({ ...input, hideAvailableBalance: !val })}
                                value={!input.hideAvailableBalance}
                                disabled={userRole != UserTypes.ADMIN || (input.hideWithdrawableBalance && !input.hideAvailableBalance)}
                            />
                            <CheckboxSelection
                                name={t('configuration.balanceDisplay.withdrawableBalance')}
                                customClass='balance-control-box'
                                output={(val: boolean) => setInput({ ...input, hideWithdrawableBalance: !val })}
                                value={!input.hideWithdrawableBalance}
                                disabled={userRole != UserTypes.ADMIN || (input.hideAvailableBalance && !input.hideWithdrawableBalance)}
                            />
                        </FlexRow>
                    </CustomContainer>
                </CustomContainer>
                <CustomContainer topMargin='2'>
                    <AppLabel
                        color='#101828' textSize='1.8'
                        fontWeight='700' bottomMargin='2'
                    >
                        {t('configuration.liveChatLabel')}
                    </AppLabel>

                    <FormGroupWithIcon>
                        <label>{t('configuration.ebanqoAgentCode.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.ebanqoAgentCode.placeholder')}
                                name='ebanqoAgentCode'
                                value={input.ebanqoAgentCode}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.ebanqoAgentCode.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.ebanqoUrlPath.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.ebanqoUrlPath.placeholder')}
                                name='ebanqoUrlPath'
                                value={input.ebanqoUrlPath}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.ebanqoUrlPath.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.ebanqoUsername.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.ebanqoUsername.placeholder')}
                                name='ebanqoUsername'
                                value={input.ebanqoUsername}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.ebanqoUsername.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                    <FormGroupWithIcon>
                        <label>{t('configuration.ebanqoPassword.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.ebanqoPassword.placeholder')}
                                name='ebanqoPassword'
                                type={showPassword ? 'text' : 'password'}
                                value={input.ebanqoPassword}
                                onChange={handleInput}
                                autoComplete='off'
                                required
                                disabled={inputsDisabled}
                            />
                            {!showPassword ?
                                <AbsoluteContainer bottom='2' right='3'>
                                    <AppSpan
                                        color='rgba(0, 0, 0, 0.5)' cursor='pointer'
                                        onClick={() => setShowPassword(true)}
                                    >
                                        <BsEyeSlash fontSize='1.7rem' />
                                    </AppSpan>
                                </AbsoluteContainer>
                                :
                                <AbsoluteContainer bottom='2' right='3'>
                                    <AppSpan
                                        color='rgba(0, 0, 0, 0.5)' cursor='pointer'
                                        onClick={() => setShowPassword(false)}
                                    >
                                        <BsEye fontSize='1.7rem' />
                                    </AppSpan>
                                </AbsoluteContainer>
                            }
                            <CustomContainer width='auto' sizeUnit='' className="input-with-icon-tag" >
                                {explain(
                                    t('configuration.ebanqoPassword.tooltip'), true, '0'
                                )}
                            </CustomContainer>
                        </div>
                    </FormGroupWithIcon>
                </CustomContainer>



                <CustomContainer topMargin='2'>
                    <AppLabel
                        color='#101828' textSize='1.8'
                        fontWeight='700'
                    >
                        {t('configuration.advanced')}
                    </AppLabel>
                    <CustomContainer>
                        <AppLabel
                            color='#9e0303' textSize='1.4'
                            fontWeight='700' bottomMargin='2'
                        >
                            {t('configuration.warnings.advancedSettings')}
                        </AppLabel>
                    </CustomContainer>


                    <FormGroup>
                        <label> {t('configuration.accountNumberLength.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.accountNumberLength.placeholder')}
                                name='accountNumberLength'
                                value={input.accountNumberLength}
                                onChange={handleInputLength}
                                autoComplete='off'
                                required={false}
                                disabled={inputsDisabled}
                                type='number'
                                max={20}
                            />
                        </div>
                    </FormGroup>
                    <FormGroup>
                        <label>{t('configuration.phoneNumberLength.label')}</label>
                        <div className="input-with-icon">
                            <input
                                placeholder={t('configuration.phoneNumberLength.placeholder')}
                                name='phoneNumberLength'
                                value={input.phoneNumberLength}
                                onChange={handleInputLength}
                                autoComplete='off'
                                required={false}
                                disabled={inputsDisabled}
                                type='number'
                                max={20}
                            />
                        </div>
                    </FormGroup>
                    <FlexRow
                        justifyContent='space-between' wrap='wrap'
                        className='control-box-container'
                    >
                        <CheckboxSelection
                            name={t('configuration.hidePaymentItems')}
                            customClass='balance-control-box'
                            output={() => setInput((prevInput) => ({
                                ...prevInput,
                                paymentItemIsHidden: !prevInput.paymentItemIsHidden
                            }))}
                            value={input.paymentItemIsHidden}
                            disabled={userRole != UserTypes.ADMIN}
                        />
                        <CheckboxSelection
                            name={t('configuration.airtimeAsBills')}
                            customClass='balance-control-box'
                            output={() => setInput((prevInput) => ({
                                ...prevInput,
                                useAirtimeAsBills: !prevInput.useAirtimeAsBills
                            }))}
                            value={input.useAirtimeAsBills}
                            disabled={userRole != UserTypes.ADMIN}
                        />
                    </FlexRow>
                    <FlexRow
                        justifyContent='space-between' wrap='wrap'
                        className='control-box-container'
                    >
                        <CheckboxSelection
                            name={t('configuration.liveChat')}
                            customClass='balance-control-box'
                            output={() => setInput((prevInput) => ({
                                ...prevInput,
                                hasLiveChat: !prevInput.hasLiveChat
                            }))}
                            value={input.hasLiveChat}
                            disabled={userRole != UserTypes.ADMIN}
                        />
                        <CheckboxSelection
                            name={t('configuration.loginWithPin')}
                            customClass='balance-control-box'
                            output={(val: boolean) => setInput((prevInput) => ({
                                ...prevInput,
                                usePINforLogin: !prevInput.usePINforLogin
                            }))}
                            value={input.usePINforLogin}
                            disabled={userRole != UserTypes.ADMIN}
                        />
                    </FlexRow>

                </CustomContainer>

                {userRole === UserTypes.ADMIN &&
                    <FlexRow justifyContent='flex-end' topMargin='3'>
                        <Button
                            width='8'
                            titleSize='1.4' fontWeight='600'
                            height='4' radius='0.8'
                            bottomMargin='3.5' hoverColor='#0D968F'
                            hoverBgColor='#fff' borderColor='#0D968F'
                            onClick={handleSubmit}
                        >
                            {t('configuration.buttons.submit')}
                        </Button>
                    </FlexRow>
                }
            </Form>
            <TermsAcceptanceModal
                open={termsOpen}
                onClose={handleDeclineTerms}
                onAccept={handleAcceptTerms}
                termsUrl={ONBOARDING_TERMS_URL}
            />
            <NinEnforcementModal
                open={ninModalOpen}
                onClose={handleDeclineNin}
                onConfirm={handleConfirmNin}
            />
            <ExemptVerificationModal
                open={exemptModalOpen}
                onClose={handleDeclineExempt}
                onConfirm={handleConfirmExempt}
            />
        </ScreenContainer>
    )
}

export default utility.routeData('configuration', 'Configuration', <Configuration />)